import Control from '@/Controls/Control';
import ComponentManager from '@/Management/ComponentManager';
import RoleAssignmentListViewModelType from '@/ViewModels/Controls/RoleAssignmentListViewModel';
import CoreFrameGridType from '@/Controls/CoreFrameGridControl';
import CoreFrameGridViewModelType from '@/ViewModels/Controls/CoreFrameGridViewModel';
import Utilities from '@/Utility/Utilities';

export default class RoleAssignmentList extends Control {

    private idToCheckBox: Array<any>;
    private jQueryControl: JQuery;

    private CoreFrameGridViewModel = ComponentManager.getViewModel<typeof CoreFrameGridViewModelType>('CoreFrameGridViewModel');
    private CoreFrameGrid = ComponentManager.getControl<typeof CoreFrameGridType>('CoreFrameGrid');

    public constructor(private viewModel: RoleAssignmentListViewModelType, private element?: JQuery<HTMLElement>) {
        super(viewModel, element);
        this.idToCheckBox = this.paramOrDefault([]);

        this.onWidgetCreated(this.widgetCreatedCallback.bind(this));
    }

    /** 
     * @override
     */
    public getControl(): JQuery {
        if (this.jQueryControl != null) {
            return this.jQueryControl;
        }
        this.jQueryControl = this.element || $('<div>');

        const gridViewModel = new this.CoreFrameGridViewModel({
            columns: this.viewModel.columns,
            data: this.viewModel.dataSource,
            isScrollable: true
        });
        const grid = new this.CoreFrameGrid(gridViewModel, $('<div>'));
        const gridControl = grid.getControl();

        this.jQueryControl.append(gridControl);

        this.widget(null);

        return this.jQueryControl;
    }

    private widgetCreatedCallback(widget: null) {
        this.applyBindings(this.jQueryControl, {
            controlName: 'roleassignmentlist',
            loader: this.viewModel.isBusy,
            controlIdent: this.identifier,
            tooltip: this.viewModel.tooltip,
            invalid: this.viewModel.invalidationText,
            visible: this.viewModel.isVisible,
            styles: this.definePureComputed({
                read: () => {
                    return [];
                }
            }),
            states: this.definePureComputed({
                read: () => {
                    let states = [];

                    if (this.viewModel.isRequired() === true) {
                        states.push('required');
                    }

                    if (this.viewModel.isEnabled() === true) {
                        states.push('enabled');
                    } else {
                        states.push('disabled');
                    }

                    return states;
                }
            }),
            locator: this.viewModel.locator
        });

        this.addEventHandler('click', this.jQueryControl, this.clickCallback.bind(this), '.checkbox');

        this.initializeSelectAllCheckBoxes();
    }

    private clickCallback(e: JQuery.ClickEvent, setChecked: boolean = false, checked: boolean = false) {
        const $checkbox = $(e.target) as JQuery<HTMLInputElement>;
        if (setChecked)
            $checkbox.prop('checked', checked);
        const principalId = $checkbox.data('principal') + '';
        const group = $checkbox.data('group') + '';
        const isChecked = $checkbox.is(':checked');

        const $selectAllCheckbox = this.getSelectAllCheckBoxForGroup(group);
        const $groupCheckboxes = this.getGroupCheckBoxes(group);

        if ($checkbox.is($selectAllCheckbox)) {
            this.onSelectAllCheckBoxChanged($selectAllCheckbox, $groupCheckboxes);
        } else {
            this.onGroupCheckBoxChanged($checkbox, $selectAllCheckbox, $groupCheckboxes);
        }

        if (principalId === '-1' && (this.viewModel as any).lockPrincipals) {
            this.toggleEnableStateOfCheckbox(principalId, group, isChecked);
        }

        if (Utilities.isFunction(e.stopPropagation))
            e.stopPropagation();
    }

    private toggleEnableStateOfCheckbox(principalId, group: string, disable: boolean): void {
        const cbs = this.jQueryControl.find(`[data-group="${group}"]`);
        cbs.each((i: number, cb: HTMLElement) => {
            if (principalId != '-1') {
                if (disable) {
                    $(cb).prop('disabled', true);
                    $(cb).prop('checked', true);
                } else {
                    $(cb).removeAttr('disabled');
                }
            }
        });
    }

    private initializeSelectAllCheckBoxes(): void {
        const $gridRows = this.jQueryControl.find('.cf-grid-row');

        if ($gridRows.length <= 2)
            return;

        const $firstPrincipalRowCheckBoxes = $($gridRows[2]).find('.checkbox');

        $firstPrincipalRowCheckBoxes.each((idx, gcb) => {
            const $gcb = $(gcb);
            const group = $gcb.data('group') + '';
            const $selectAllCheckBox = this.getSelectAllCheckBoxForGroup(group);
            const $groupCheckBoxes = this.getGroupCheckBoxes(group);
            this.synchronizeSelectAllCheckBox($selectAllCheckBox, $groupCheckBoxes, $gcb.is(':checked'));
        });
    }

    /**
     * Wird beim Klick auf die Gesamtauswahl ausgeführt
     * @param $selectAllCheckbox Checkbox für Gesamtauswahl
     * @param $groupCheckboxes Alle Checkboxen der Gruppe
     */
    private onSelectAllCheckBoxChanged($selectAllCheckbox: JQuery<HTMLInputElement>, $groupCheckboxes: JQuery<HTMLInputElement>): void {
        if ($selectAllCheckbox[0].indeterminate) {
            $groupCheckboxes.each((idx, cb) => {
                const $cb = $(cb);
                if ($cb.is(':checked'))
                    this.clickCallback({target: $cb} as any, true, false);
            });
            $selectAllCheckbox[0].indeterminate = false;
        } else {
            const newState = $selectAllCheckbox.is(':checked');
            $groupCheckboxes.each((idx, cb) => {
                const $cb = $(cb);
                if ($cb.is(':checked') !== newState)
                    this.clickCallback({ target: $cb } as any, true, newState);
            });
        }
    }

    /**
     * Wird beim Klick auf eine Checkbox ausgeführt
     * @param $checkbox Angeklickte Checkbox
     * @param $selectAllCheckbox Checkbox für Gesamtauswahl
     * @param $groupCheckboxes Alle Checkboxen der Gruppe
     */
    private onGroupCheckBoxChanged($checkbox: JQuery<HTMLInputElement>, $selectAllCheckbox: JQuery<HTMLInputElement>, $groupCheckboxes: JQuery<HTMLInputElement>): void {
        const group = $checkbox.data('group') + '';
        const principalId = $checkbox.data('principal') + '';

        const newState = $checkbox.is(':checked');

        this.synchronizeSelectAllCheckBox($selectAllCheckbox, $groupCheckboxes, newState);

        this.viewModel.onCheck(principalId, group, newState);
    }

    private synchronizeSelectAllCheckBox($selectAllCheckbox: JQuery<HTMLInputElement>, $groupCheckboxes: JQuery<HTMLInputElement>, newState: boolean): void {
        if ($selectAllCheckbox.length === 0)
            return;
        const checkBoxesWithOtherState = $groupCheckboxes.filter((idx, cb) => {
            return $(cb).is(':checked') !== newState;
        });

        const isIndeterminated = checkBoxesWithOtherState.length > 0;
        $selectAllCheckbox[0].indeterminate = isIndeterminated;

        if (!isIndeterminated)
            $selectAllCheckbox.attr('checked', newState as any);
    }

    private getSelectAllCheckBoxForGroup(group: string): JQuery<HTMLInputElement> {
        return this.jQueryControl.find(`#cb_all_${group}`) as JQuery<HTMLInputElement>;
    }

    private getGroupCheckBoxes(group: string): JQuery<HTMLInputElement> {
        const $selectAllCheckbox = this.getSelectAllCheckBoxForGroup(group);
        const $groupCheckboxes = this.jQueryControl.find(`.checkbox[data-group="${group}"]`).not($selectAllCheckbox);

        return $groupCheckboxes as JQuery<HTMLInputElement>;
    }

    public disableCheckBoxByID(ids: Array<string>): void {
        this.iterateOverCBs(ids, (cb: HTMLElement) => {
            $(cb).attr('disabled', 'disabled');
        });
    }

    public hideCheckBoxByID(ids): void {
        this.iterateOverCBs(ids, (cb: HTMLElement) => {
            $(cb).css('display', 'none');
        });
    }

    public enableCheckBoxByID(ids): void {
        this.iterateOverCBs(ids, (cb: HTMLElement) => {
            $(cb).removeAttr('disabled');
        });
    }

    public reload(): JQueryPromise<void> {
        return $.Deferred().promise();
    }

    private iterateOverCBs(ids, doWithEach: (cb: HTMLElement) => void): void {
        if (typeof doWithEach !== 'function')
            return;

        if (typeof ids === 'string') {
            ids = [ids];
        }

        ids.forEach((id: string) => {
            const cb = this.idToCheckBox[id];
            if (cb) {
                doWithEach(cb);
            }
        });
    }
}

ComponentManager.setControl('RoleAssignmentList', RoleAssignmentList, 'RADPresentation.HTMLClient.DefaultViewExtensions');